<template>
  <Combobox
    v-model="selectedValue"
    as="div"
    :nullable="nullable"
    @update:model-value="query = ''"
  >
    <ComboboxLabel class="block select-none text-base/6 text-slate-950 sm:text-sm/6 font-medium">{{ label }}</ComboboxLabel>

    <div class="relative mt-2">
      <ComboboxInput
        :class="clsx(
          [
            // Basic layout
            'relative block w-full appearance-none rounded-lg px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
            // Typography
            'text-base/6 text-slate-950 placeholder:text-slate-500 sm:text-sm/6',
            // Border
            'border',
            // Background color
            'bg-transparent',
            // Hide default focus styles
            'focus:outline-none focus:shadow-none focus:ring-0',
            // Disabled state
            '[disabled]:border-slate-950/20',
          ],
          error ? '!border-error hover:!border-error' : 'border-slate-950/10 hover:border-slate-950/20'
        )"
        :display-value="(option) => (option as any)?.label" @change="query = $event.target.value"
        @blur="query = ''"
      />

      <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
        <span class="h-5 w-5 text-slate-400 i-heroicons-chevron-up-down-20-solid" aria-hidden="true" />
      </ComboboxButton>

      <ComboboxOptions
        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
      >
        
        <slot name="first-option" />
        <ComboboxOption
          v-for="option in filteredOptions"
          :key="option.value"
          v-slot="{ active, selected }"
          :value="option"
          as="template"
        >
          <li :class="['relative cursor-default select-none py-2 pl-8 pr-4', active ? 'bg-secondary text-primary-800' : 'text-slate-900']">

            <span :class="['block truncate', selected && 'font-semibold']">
              {{ option.label }}
            </span>

            <span
              v-if="selectedValue ? selectedValue.value === option.value : false"
              :class="['absolute inset-y-0 left-0 flex items-center pl-1.5']"
            >
              <span class="h-5 w-5 i-heroicons-check" aria-hidden="true" />
            </span>
          </li>
        </ComboboxOption>
      </ComboboxOptions>
    </div>

    <p
      v-if="description"
      :class="clsx(
        ['mt-2 text-base/6 text-slate-500 sm:text-sm/6'],
        $attrs.disabled && 'opacity-50'
      )"
    >
      {{ description }}
    </p>

    <p
      v-if="error && !noErrorMessages"
      :class="clsx(
        ['text-base/6 text-error sm:text-sm/6'],
        $attrs.disabled && 'opacity-50',
        !description && 'mt-2'
      )"
    >
      {{ error }}
    </p>
  </Combobox>
</template>

<script lang="ts" setup>
import { computed, PropType, ref } from 'vue'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import clsx from 'clsx'

type Option = { label: string; value: string; disabled?: boolean; [key: string]: any }

const selectedValue = defineModel({
  type: Object as PropType<Option>,
  default: null,
})

const props = defineProps({
  label: {
    type: String,
    default: ''
  },
  options: {
    type: Array as PropType<Option[]>,
    default: () => []
  },
  description: {
    type: String,
    default: ''
  },
  error: {
    type: String as PropType<any>,
    default: ''
  },
  nullable: {
    type: Boolean,
    default: true
  },
  noErrorMessages: {
    type: Boolean,
    default: false
  }
})

const query = ref('')
const filteredOptions = computed(() =>
  query.value === ''
    ? props.options
    : props.options.filter((option) => option.label.toLowerCase().includes(query.value.toLowerCase()))
)
</script>
